/**
 * Padlock configurator (native replacement for the VO Product Options app).
 *
 * The money part. Liquid line-item properties cannot change price, so a paid
 * option is carried by a companion line of a hidden CHF 1 unit product at
 * quantity = the surcharge in CHF. That is the shape the store's real orders
 * already have, so nothing downstream changes.
 *
 * Two invariants, and they are the reason this file exists rather than a few
 * lines inside product-form.js:
 *
 *   1. A surcharge line must never outlive the padlock it belongs to. Every
 *      companion carries _config_parent, and cart changes are reconciled against
 *      it, so removing a padlock removes its surcharge and changing the padlock's
 *      quantity rescales it.
 *   2. What the customer reads and what they pay come from the same numbers.
 *      The running total in the form is computed from the same per-option prices
 *      used to build the cart line.
 *
 * The padlock itself is still added by the theme's own product-form element, so
 * the cart drawer, error handling and analytics are untouched. This listens for
 * that add and then attaches the companion.
 */
class ProductConfigurator extends HTMLElement {
  connectedCallback() {
    this.unitVariantId = this.dataset.unitVariant;
    // One unit's price in the visitor's currency, in cents (snippets/dc-unit-price.liquid): the
    // surcharges are counted in units, and a unit is CHF 1.00 but €1.95 on .de/.eu.
    this.unitCents = parseInt(this.dataset.unitPrice, 10) || 100;
    this.numberingPrice = parseInt(this.dataset.numberingPrice, 10) || 0;
    this.keyPrices = (this.dataset.keyPrices || "")
      .split(",")
      .map((n) => parseInt(n, 10) || 0);
    this.productTitle = this.dataset.productTitle || "";

    this.form = document.getElementById(this.dataset.form);
    this.numberingYes = this.querySelector('[data-numbering="yes"]');
    this.numberingNo = this.querySelector('[data-numbering="no"]');
    this.numberingText = this.querySelector("[data-numbering-text]");
    this.numberingInput = this.numberingText?.querySelector("input");
    this.keyedAlike = this.querySelector('[data-key-type="alike"]');
    this.keysField = this.querySelector("[data-keys-field]");
    this.keysSelect = this.querySelector("[data-keys-select]");
    this.totalEl = this.querySelector("[data-config-total]");

    this.addEventListener("change", this.onChange.bind(this));

    // The numbering card is the label of the "Yes" radio, and a radio cannot be unchecked by a
    // second click — so once on, numbering could not be turned off (Sam, 2026-09-22). A click on
    // the card while Yes is checked selects the hidden "No" radio instead. The synthetic click
    // the label sends to its input is ignored, or the first click would undo itself.
    const yesCard = this.numberingYes?.closest("label");
    if (yesCard && this.numberingNo) {
      yesCard.addEventListener("click", (e) => {
        if (e.target === this.numberingYes || !this.numberingYes.checked) return;
        e.preventDefault();
        this.numberingNo.checked = true;
        this.numberingNo.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    // The theme's product-form adds the padlock; attach the companion after.
    this.onItemAdd = this.handleItemAdd.bind(this);
    document.addEventListener("cart:item-add", this.onItemAdd);

    this.onChange();
  }

  disconnectedCallback() {
    document.removeEventListener("cart:item-add", this.onItemAdd);
  }

  /** Reveal conditional fields and recompute the running total. */
  onChange() {
    const numbering = this.numberingYes?.checked === true;
    if (this.numberingText) {
      this.numberingText.hidden = !numbering;
      // Disabled while hidden so an empty property is never posted, and a
      // disabled input is exempt from `required`, so browser validation only
      // applies while the field is actually on screen. Every bcpo option is
      // required today, and paying CHF 1 for numbering with nothing to engrave
      // would be an unfulfillable order.
      if (this.numberingInput) this.numberingInput.disabled = !numbering;
    }

    // Keys per padlock is offered for every keying (Sam, 2026-09-21): a keyed-different padlock
    // can ship with spare keys too. The field stays visible and enabled, and its surcharge is
    // charged whatever the key type.
    if (this.keysField) {
      this.keysField.hidden = false;
      if (this.keysSelect) this.keysSelect.disabled = false;
    }

    this.renderTotal();
  }

  /** Surcharge in whole currency units for the current selection. */
  surcharge() {
    let total = 0;

    if (this.numberingYes?.checked) total += this.numberingPrice;

    if (this.keysSelect && !this.keysSelect.disabled) {
      const option = this.keysSelect.selectedOptions[0];
      total += parseInt(option?.dataset.surcharge, 10) || 0;
    }

    return total;
  }

  renderTotal() {
    if (!this.totalEl) return;

    const amount = this.surcharge();
    if (amount <= 0) {
      this.totalEl.hidden = true;
      this.totalEl.textContent = "";
      return;
    }

    // Units times the unit's own price in this market, formatted by the same money
    // helper the rest of the theme uses, so the page shows what the cart charges.
    this.totalEl.hidden = false;
    // [price] rather than a Liquid-looking token: the label is produced by the
    // `t` filter in Liquid, and a literal {{ }} inside a Liquid output is a
    // syntax error.
    this.totalEl.textContent = (this.dataset.totalLabel || "[price]").replace(
      "[price]",
      this.formatMoney(amount * this.unitCents),
    );
  }

  /**
   * Format using the shop's own money format, handed over from Liquid on the
   * element rather than guessed here, so the thousands and decimal separators
   * follow the market (CHF on .ch, EUR on .de and .eu) with no currency logic
   * in JavaScript. Supports the four Shopify amount tokens.
   */
  formatMoney(cents) {
    const format = this.dataset.moneyFormat || "{{amount}}";
    const value = cents / 100;

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
      switch (token) {
        case "amount":
          return this.withSeparators(value.toFixed(2), ",", ".");
        case "amount_no_decimals":
          return this.withSeparators(Math.round(value).toString(), ",", "");
        case "amount_with_comma_separator":
          return this.withSeparators(value.toFixed(2), ".", ",");
        case "amount_no_decimals_with_comma_separator":
          return this.withSeparators(Math.round(value).toString(), ".", "");
        default:
          return value.toFixed(2);
      }
    });
  }

  withSeparators(number, thousands, decimal) {
    const [whole, fraction] = number.split(".");
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  }

  /**
   * The companion line's properties. Hidden ones (leading underscore) carry the
   * numbers the cart and the sync need; visible ones read on the checkout page in
   * the customer's language, named with the same locale strings as the padlock's.
   */
  companionProperties({ parentKey, amount, numbering, keys, keysLabel, forText }) {
    const per = this.dataset.perPadlock || "";
    const properties = {
      _config_parent: parentKey,
      _config_unit: String(amount),
      _config_numbering: String(numbering),
      _config_keys: String(keys),
      [this.dataset.forLabel || "For"]: forText,
    };
    if (numbering > 0 && this.dataset.propNumbering) {
      properties[this.dataset.propNumbering] = `${this.formatMoney(numbering * this.unitCents)} ${per}`.trim();
    }
    if (keys > 0 && this.dataset.propKeys) {
      properties[this.dataset.propKeys] = `${keysLabel ? keysLabel + ", " : ""}${this.formatMoney(keys * this.unitCents)} ${per}`.trim();
    }
    return properties;
  }

  /**
   * After the padlock lands in the cart, add its surcharge line.
   *
   * The event carries the added item, whose `key` identifies the exact cart line
   * including its properties, so the companion can point at it unambiguously even
   * when the same variant is in the cart twice with different options.
   */
  handleItemAdd(event) {
    const amount = this.surcharge();
    if (amount <= 0) return;

    // Only this buy box's own add. The bulk matrix dispatches the same event for the same
    // variant, and answering it put this form's options on the matrix's padlock as a second,
    // paid companion (Sam, 2026-10-01).
    const context = event.detail?.context;
    if (context === "BulkMatrix") return;
    const ownContext = this.form?.dataset.eventContext;
    if (ownContext && context !== ownContext) return;

    const items = event.detail?.items || [];
    const added = items[0];
    if (!added) return;

    // Only react to this product's own add, not to any other card on the page.
    const addedId = String(added.variant_id || added.id || "");
    const formVariant = this.form?.querySelector("[name=id]")?.value;
    if (formVariant && addedId && formVariant !== addedId) return;

    const quantity = (added.quantity || 1) * amount;

    // _config_unit is the per-padlock surcharge, so cart-surcharge-sync can
    // rescale this line when the padlock's quantity changes without having to
    // know anything about which options were chosen. _config_numbering and
    // _config_keys split it so the cart can itemise the charges under the padlock
    // (Sam, 2026-09-22). The visible properties are what the checkout shows, where
    // this line cannot be folded into the padlock's: they say what it is for.
    const numbering = this.numberingYes?.checked ? this.numberingPrice : 0;
    const keys = amount - numbering;
    const properties = this.companionProperties({
      parentKey: added.key,
      amount,
      numbering,
      keys,
      keysLabel: this.keysSelect?.selectedOptions[0]?.textContent.trim() || "",
      forText: [added.product_title || this.productTitle, added.variant_title]
        .filter(Boolean)
        .join(", "),
    });

    fetch(window.routes.cart_add_url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [{ id: this.unitVariantId, quantity, properties }],
      }),
    })
      .then((r) => r.json())
      .then(() => {
        document.dispatchEvent(new CustomEvent("cart:surcharge-added"));
      })
      .catch((e) => console.error("configurator surcharge:", e));
  }
}

customElements.define("product-configurator", ProductConfigurator);
